import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getItemCountString } from "../../app-utils/string.js";
import { type Configuration } from "../../graphql.js";
import { filterByLicense } from "../../redux/modules/config.js";
import { type IUserGroupAdminState, loadUserGroupAdminCounts } from "../../redux/modules/user-group-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import {
  CONTENT_BROWSER_FEATURE_TYPE_ORGANISMS,
  CONTENT_BROWSER_FEATURE_TYPE_SAMPLE_TYPES,
  CONTENT_BROWSER_FEATURE_TYPE_USERS,
  ENTITY_TYPE,
  GROUP_BY_ATTRIBUTE_CREATION_TIME,
  GROUP_BY_ATTRIBUTE_USER_GROUP,
  LICENSE_MODULE,
} from "../../settings/settings.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import UserGroupAdminPage from "./UserGroupAdminPage.js";

export const USER_GROUP_ADMIN_FEATURES: INavPanelCategory[] = [
  {
    label: "Genomes",
    tooltip: "Browse through the genomes",
    licenseModule: LICENSE_MODULE.EXPLORER,
    eventKey: CONTENT_BROWSER_FEATURE_TYPE_ORGANISMS,
    navItems: [
      {
        label: "User Groups",
        route: `organisms/${GROUP_BY_ATTRIBUTE_USER_GROUP}/${ENTITY_TYPE.ORGANISM}`,
        eventKey: GROUP_BY_ATTRIBUTE_USER_GROUP,
        default: true,
        pageComponent: <UserGroupAdminPage />,
      },
      {
        label: "Creation Time",
        route: `organisms/${GROUP_BY_ATTRIBUTE_CREATION_TIME}/${ENTITY_TYPE.ORGANISM}`,
        eventKey: GROUP_BY_ATTRIBUTE_CREATION_TIME,
        pageComponent: <UserGroupAdminPage />,
      },
    ],
  },
  {
    label: "Sample Types",
    tooltip: "Browse through the sample types",
    licenseModule: LICENSE_MODULE.TRACKER,
    eventKey: CONTENT_BROWSER_FEATURE_TYPE_SAMPLE_TYPES,
    navItems: [
      {
        label: "User Groups",
        route: `sampleTypes/${GROUP_BY_ATTRIBUTE_USER_GROUP}/${ENTITY_TYPE.SAMPLE_TYPE}`,
        eventKey: GROUP_BY_ATTRIBUTE_USER_GROUP,
        default: true,
        pageComponent: <UserGroupAdminPage />,
      },
    ],
  },
  {
    label: "Users",
    tooltip: "Browse through the users",
    eventKey: CONTENT_BROWSER_FEATURE_TYPE_USERS,
    navItems: [
      {
        label: "User Groups",
        route: `users/${GROUP_BY_ATTRIBUTE_USER_GROUP}/${ENTITY_TYPE.USER}`,
        eventKey: GROUP_BY_ATTRIBUTE_USER_GROUP,
        pageComponent: <UserGroupAdminPage />,
      },
    ],
  },
];

/**
 * Admin User Groups main component
 */
function ProjectGroupsAdminPage(): JSX.Element {
  const dispatch = useDispatch<ReduxDispatch>();
  const { content } = useSelector<IAppState, IUserGroupAdminState>((state) => state.userGroupAdmin);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);

  useEffect(() => {
    const projectGroupAdminKeys = filterByLicense(USER_GROUP_ADMIN_FEATURES, configuration).map(
      (category) => category.eventKey,
    );
    dispatch(loadUserGroupAdminCounts(projectGroupAdminKeys));
  }, [configuration, dispatch]);

  function getNavItems(): INavPanelCategory[] {
    return filterByLicense(USER_GROUP_ADMIN_FEATURES, configuration).map((category) => ({
      ...category,
      badge: getItemCountString(content, category.eventKey),
    }));
  }

  return <PageLayout navPanelCategories={getNavItems()} />;
}

export default ProjectGroupsAdminPage;
